import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import etcsdmi from "../assets/etcsdmi.jpg";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { openProtectedFile } from "../api/openProtectedFile";

const statusOptions = [
  { value: "not_started", label: "Not started", className: "text-bg-secondary" },
  { value: "in_progress", label: "In progress", className: "text-bg-primary" },
  { value: "complete", label: "Complete", className: "text-bg-success" },
  { value: "blocked", label: "Blocked", className: "text-bg-danger" },
  { value: "not_applicable", label: "Not applicable", className: "text-bg-light text-dark border" }
];

const requirementCoverageConfig = {
  covered: { label: "Covered", color: "#198754" },
  notCovered: { label: "Not covered", color: "#adb5bd" }
};

const systemRequirementCategoryConfig = {
  SW: { label: "SW", color: "#0d6efd" },
  SWext: { label: "SWext", color: "#6610f2" },
  HW: { label: "HW", color: "#fd7e14" },
  mechanical: { label: "Mechanical", color: "#6f42c1" },
  definition: { label: "Definition", color: "#ffc107" },
  informative: { label: "Informative", color: "#0dcaf0" },
  others: { label: "Others", color: "#6c757d" }
};

const systemRequirementLinkConfig = {
  linked: { label: "Linked", color: "#198754" },
  unlinked: { label: "Unlinked", color: "#adb5bd" }
};

const testResultConfig = {
  passed: { label: "Passed", color: "#198754" },
  failed: { label: "Failed", color: "#dc3545" },
  blocked: { label: "Blocked", color: "#fdc43f" },
  not_run: { label: "Not run", color: "#adb5bd" },
  inconclusive: { label: "Inconclusive", color: "#0dcaf0" }
};

function getStatusOption(status) {
  return statusOptions.find((option) => option.value === status) ?? statusOptions[0];
}

function getPhaseBarClass(status) {
  if (status === "complete") return "phase-fill-complete";
  if (status === "in_progress") return "phase-fill-progress";
  if (status === "blocked") return "phase-fill-blocked";
  if (status === "not_applicable") return "phase-fill-muted";
  return "phase-fill-empty";
}

function getPhaseBarWidth(status) {
  if (status === "complete") return "100%";
  if (status === "in_progress") return "42%";
  if (status === "blocked") return "100%";
  if (status === "not_applicable") return "100%";
  return "0%";
}

function buildSegments(counts, config) {
  const total = Object.keys(config).reduce((sum, key) => sum + Number(counts[key] ?? 0), 0);

  return {
    total,
    segments: Object.entries(config)
      .map(([key, item]) => {
        const count = Number(counts[key] ?? 0);
        const percent = total > 0 ? (count / total) * 100 : 0;

        return {
          key,
          label: item.label,
          color: item.color,
          count,
          percent
        };
      })
      .filter((segment) => segment.count > 0)
  };
}

function buildDonutBackground(segments) {
  const stops = [];
  let currentOffset = 0;

  segments.forEach((segment) => {
    const start = currentOffset;
    const end = currentOffset + segment.percent;
    stops.push(`${segment.color} ${start}% ${end}%`);
    currentOffset = end;
  });

  return segments.length ? `conic-gradient(${stops.join(", ")})` : "#e9ecef";
}

function sortByName(items) {
  return [...items].sort((left, right) => (
    String(left.name ?? "").localeCompare(
      String(right.name ?? ""),
      undefined,
      { numeric: true, sensitivity: "base" }
    )
  ));
}

function sortVersions(versions) {
  return [...versions].sort((leftVersion, rightVersion) => {
    const activeCompare = Number(Boolean(rightVersion.isActive)) - Number(Boolean(leftVersion.isActive));
    if (activeCompare !== 0) return activeCompare;

    const closedCompare = Number(Boolean(leftVersion.closed)) - Number(Boolean(rightVersion.closed));
    if (closedCompare !== 0) return closedCompare;

    return String(leftVersion.name ?? "").localeCompare(
      String(rightVersion.name ?? ""),
      undefined,
      { numeric: true, sensitivity: "base" }
    );
  });
}

function getVersionMajor(versionName) {
  const match = String(versionName ?? "").trim().match(/^(\d+)\./);
  return match?.[1] ?? null;
}

function buildVersionFilterOptions(versions) {
  const sortedVersions = sortVersions(versions);
  const majorCounts = sortedVersions.reduce((counts, version) => {
    const major = getVersionMajor(version.name);
    if (major) counts[major] = (counts[major] ?? 0) + 1;
    return counts;
  }, {});
  const majorOptions = Object.entries(majorCounts)
    .filter(([, count]) => count > 1)
    .map(([major]) => ({
      value: `major:${major}`,
      label: `${major}.x`,
      major
    }))
    .sort((left, right) => left.major.localeCompare(right.major, undefined, { numeric: true }));

  return [
    ...majorOptions,
    ...sortedVersions.map((version) => ({
      value: String(version.id),
      label: version.name
    }))
  ];
}

function getPhaseDocuments(phase, documentKeys) {
  return documentKeys.flatMap((key) => (
    (phase[key] ?? []).map((phaseDocument) => ({
      ...phaseDocument,
      phase,
      group: key
    }))
  ));
}

function getAllPhaseDocuments(phases, documentKeys) {
  return phases.flatMap((phase) => getPhaseDocuments(phase, documentKeys));
}

function isPreviousPhaseReference(phaseDocument) {
  return (
    phaseDocument.direction === "input" &&
    phaseDocument.documentType.category === "output"
  );
}

function buildLifecycleSummary(phases, documentKeys, actionFilter) {
  const statusCounts = Object.fromEntries(statusOptions.map((status) => [status.value, 0]));
  phases.forEach((phase) => {
    statusCounts[phase.manualStatus] = (statusCounts[phase.manualStatus] ?? 0) + 1;
  });

  const allPhaseDocuments = getAllPhaseDocuments(phases, documentKeys);
  const documentTypeById = new Map();
  allPhaseDocuments.forEach((phaseDocument) => {
    documentTypeById.set(phaseDocument.documentType.id, phaseDocument.documentType);
  });

  const uniqueDocumentTypes = Array.from(documentTypeById.values());
  const availableDocumentTypes = uniqueDocumentTypes.filter((documentType) => documentType.latestInstance);
  const missingPhaseDocuments = allPhaseDocuments.filter((phaseDocument) => !phaseDocument.documentType.latestInstance);
  const attentionPhases = phases.filter((phase) => (
    phase.manualStatus === "blocked" ||
    phase.manualStatus === "in_progress" ||
    (
      phase.manualStatus === "complete" &&
      getPhaseDocuments(phase, documentKeys).some((phaseDocument) => !phaseDocument.documentType.latestInstance)
    )
  ));

  const myActions = missingPhaseDocuments
    .filter((phaseDocument) => phaseDocument.documentType.canUpload)
    .filter(actionFilter);

  return {
    statusCounts,
    completePhases: statusCounts.complete ?? 0,
    totalPhases: phases.length,
    uniqueDocuments: uniqueDocumentTypes.length,
    availableDocuments: availableDocumentTypes.length,
    documentTypes: uniqueDocumentTypes,
    missingPhaseDocuments,
    attentionPhases,
    myActions
  };
}

function RequirementDonut({ title, subtitle, totalLabel, total, segments }) {
  return (
    <div className="border rounded p-3 bg-white h-100">
      <div className="mb-3">
        <div className="fw-semibold">{title}</div>
        <div className="text-muted small">{subtitle}</div>
      </div>

      <div className="d-flex align-items-center gap-3">
        <div className="position-relative requirement-donut">
          <div
            className="requirement-donut-ring"
            style={{ background: buildDonutBackground(segments) }}
          />
          <div className="requirement-donut-center">
            <div className="fw-semibold" style={{ fontSize: "1.1rem", lineHeight: 1 }}>
              {total}
            </div>
            <div className="text-muted" style={{ fontSize: "0.68rem", lineHeight: 1.1 }}>
              {totalLabel}
            </div>
          </div>
        </div>

        <div className="d-flex flex-column gap-2 small">
          {segments.length ? segments.map((segment) => (
            <div key={segment.key} className="d-inline-flex align-items-center gap-2">
              <span
                className="requirement-donut-dot"
                style={{ backgroundColor: segment.color }}
              />
              <span>
                {segment.label}: {segment.count} ({Math.round(segment.percent)}%)
              </span>
            </div>
          )) : (
            <span className="text-muted">No requirements yet.</span>
          )}
        </div>
      </div>
    </div>
  );
}

function SystemRequirementsSnapshot() {
  const [summary, setSummary] = useState({
    total: 0,
    categoryCounts: {},
    swLinkCounts: { linked: 0, unlinked: 0 }
  });
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadSummary = async () => {
      try {
        const res = await fetchWithAuth("/api/system-requirements");

        if (!res.ok) {
          const responseError = await res.json().catch(() => null);
          throw new Error(responseError?.error || "Failed to load system requirements summary");
        }

        const data = await res.json();
        const categoryCounts = Object.fromEntries(
          Object.keys(systemRequirementCategoryConfig).map((key) => [key, 0])
        );
        const swLinkCounts = { linked: 0, unlinked: 0 };

        data.forEach((requirement) => {
          if (categoryCounts[requirement.category] !== undefined) {
            categoryCounts[requirement.category] += 1;
          }

          if (requirement.category === "SW") {
            if ((requirement.SWRequirements ?? []).length > 0) {
              swLinkCounts.linked += 1;
            } else {
              swLinkCounts.unlinked += 1;
            }
          }
        });

        if (!cancelled) {
          setSummary({
            total: data.length,
            categoryCounts,
            swLinkCounts
          });
          setError("");
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      }
    };

    loadSummary();

    return () => {
      cancelled = true;
    };
  }, []);

  const categories = buildSegments(summary.categoryCounts, systemRequirementCategoryConfig);
  const swLinks = buildSegments(summary.swLinkCounts, systemRequirementLinkConfig);

  return (
    <>
      {error && (
        <div className="col-12 alert alert-warning py-2">
          System requirements summary is unavailable: {error}
        </div>
      )}

      <div className="col-lg-6">
        <RequirementDonut
          title="System requirements category"
          subtitle="Distribution by requirement category."
          totalLabel="category"
          total={categories.total || summary.total}
          segments={categories.segments}
        />
      </div>

      <div className="col-lg-6">
        <RequirementDonut
          title="SW links"
          subtitle="SW system requirements linked to SW requirements."
          totalLabel="SW links"
          total={swLinks.total}
          segments={swLinks.segments}
        />
      </div>
    </>
  );
}

function RequirementsSnapshot() {
  const [summary, setSummary] = useState({
    total: 0,
    coverageCounts: { covered: 0, notCovered: 0 }
  });
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadSummary = async () => {
      try {
        const params = new URLSearchParams({ page: "1", pageSize: "1" });
        const res = await fetchWithAuth(`/api/requirements?${params.toString()}`);

        if (!res.ok) {
          const responseError = await res.json().catch(() => null);
          throw new Error(responseError?.error || "Failed to load requirements summary");
        }

        const data = await res.json();
        if (!cancelled) {
          setSummary({
            total: data.total ?? 0,
            coverageCounts: data.coverageCounts ?? { covered: 0, notCovered: 0 }
          });
          setError("");
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      }
    };

    loadSummary();

    return () => {
      cancelled = true;
    };
  }, []);

  const coverage = buildSegments(summary.coverageCounts, requirementCoverageConfig);

  return (
    <>
      {error && (
        <div className="col-12 alert alert-warning py-2">
          Requirements summary is unavailable: {error}
        </div>
      )}

      <div className="col-lg-6">
        <RequirementDonut
          title="SW requirements coverage"
          subtitle="Coverage by linked test cases."
          totalLabel="coverage"
          total={coverage.total || summary.total}
          segments={coverage.segments}
        />
      </div>
    </>
  );
}

function TestsCoverageSnapshot() {
  const [summary, setSummary] = useState({
    total: 0,
    counts: {}
  });
  const [versions, setVersions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [versionFilter, setVersionFilter] = useState("all");
  const [deviceFilter, setDeviceFilter] = useState("all");
  const [error, setError] = useState("");

  const sortedVersions = useMemo(() => sortVersions(versions), [versions]);
  const versionFilterOptions = useMemo(() => buildVersionFilterOptions(versions), [versions]);
  const sortedDevices = useMemo(() => sortByName(devices), [devices]);

  useEffect(() => {
    let cancelled = false;

    const loadFilters = async () => {
      try {
        const [versionsRes, devicesRes] = await Promise.all([
          fetchWithAuth("/api/versions"),
          fetchWithAuth("/api/devices")
        ]);

        if (!versionsRes.ok || !devicesRes.ok) {
          throw new Error("Failed to load tests coverage filters");
        }

        const versionsData = await versionsRes.json();
        const devicesData = await devicesRes.json();

        if (!cancelled) {
          setVersions(versionsData);
          setDevices(devicesData);
        }
      } catch (err) {
        if (!cancelled) {
          setVersions([]);
          setDevices([]);
          setError(err.message);
        }
      }
    };

    loadFilters();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadSummary = async () => {
      try {
        const params = new URLSearchParams();
        if (versionFilter.startsWith("major:")) {
          params.set("versionMajor", versionFilter.replace("major:", ""));
        } else if (versionFilter !== "all") {
          params.set("versionId", versionFilter);
        }
        if (deviceFilter !== "all") params.set("deviceId", deviceFilter);
        const query = params.toString();
        const [runsRes, testCasesRes] = await Promise.all([
          fetchWithAuth(`/api/test-cases/runs${query ? `?${query}` : ""}`),
          fetchWithAuth("/api/test-cases?compact=1")
        ]);

        if (!runsRes.ok || !testCasesRes.ok) {
          throw new Error("Failed to load tests coverage");
        }

        const runsData = await runsRes.json();
        const testCasesData = await testCasesRes.json();
        const runs = runsData.rows ?? [];
        const runTestCaseIds = new Set(runs.map((run) => Number(run.testCaseId)));
        const counts = Object.fromEntries(Object.keys(testResultConfig).map((key) => [key, 0]));

        runs.forEach((run) => {
          counts[run.result] = (counts[run.result] ?? 0) + 1;
        });

        testCasesData.forEach((testCase) => {
          if (!runTestCaseIds.has(Number(testCase.id))) {
            counts.not_run += 1;
          }
        });

        if (!cancelled) {
          setSummary({
            total: runs.length + testCasesData.length - runTestCaseIds.size,
            counts
          });
          setError("");
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      }
    };

    loadSummary();

    return () => {
      cancelled = true;
    };
  }, [deviceFilter, versionFilter]);

  const { total, segments } = buildSegments(summary.counts, testResultConfig);

  return (
    <div className="col-lg-6">
      <div className="border rounded p-3 bg-white h-100">
        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
          <div>
            <div className="fw-semibold">Test coverage</div>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <select
              className="form-select form-select-sm"
              value={versionFilter}
              onChange={(event) => setVersionFilter(event.target.value)}
              style={{ width: "130px" }}
              aria-label="Filter tests coverage by SW version"
            >
              <option value="all">All SW</option>
              {versionFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              className="form-select form-select-sm"
              value={deviceFilter}
              onChange={(event) => setDeviceFilter(event.target.value)}
              style={{ width: "130px" }}
              aria-label="Filter tests coverage by device"
            >
              <option value="all">All devices</option>
              {sortedDevices.map((device) => (
                <option key={device.id} value={device.id}>
                  {device.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="alert alert-warning py-2">
            Test coverage is unavailable: {error}
          </div>
        )}

        <div className="d-flex align-items-center gap-3">
          <div className="position-relative requirement-donut">
            <div
              className="requirement-donut-ring"
              style={{ background: buildDonutBackground(segments) }}
            />
            <div className="requirement-donut-center">
              <div className="fw-semibold" style={{ fontSize: "1.1rem", lineHeight: 1 }}>
                {total || summary.total}
              </div>
              <div className="text-muted" style={{ fontSize: "0.68rem", lineHeight: 1.1 }}>
                tests
              </div>
            </div>
          </div>

          <div className="d-flex flex-column gap-2 small">
            {segments.length ? segments.map((segment) => (
              <div key={segment.key} className="d-inline-flex align-items-center gap-2">
                <span
                  className="requirement-donut-dot"
                  style={{ backgroundColor: segment.color }}
                />
                <span>
                  {segment.label}: {segment.count} ({Math.round(segment.percent)}%)
                </span>
              </div>
            )) : (
              <span className="text-muted">No test results yet.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function LifecyclePanel({
  title,
  subtitle,
  endpoint,
  route,
  documentKeys,
  missingLabels,
  actionLabel,
  actionFilter
}) {
  const [phases, setPhases] = useState([]);
  const [error, setError] = useState("");
  const [actionsExpanded, setActionsExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadLifecycle = async () => {
      try {
        const res = await fetchWithAuth(endpoint);

        if (!res.ok) {
          const responseError = await res.json().catch(() => null);
          throw new Error(responseError?.error || `Failed to load ${title}`);
        }

        const data = await res.json();
        if (!cancelled) {
          setPhases(data);
          setError("");
        }
      } catch (err) {
        if (!cancelled) {
          setPhases([]);
          setError(err.message);
        }
      }
    };

    loadLifecycle();

    return () => {
      cancelled = true;
    };
  }, [endpoint, title]);

  const summary = useMemo(() => (
    buildLifecycleSummary(phases, documentKeys, actionFilter)
  ), [phases, documentKeys, actionFilter]);

  const phaseProgress = summary.totalPhases
    ? Math.round((summary.completePhases / summary.totalPhases) * 100)
    : 0;
  const documentProgress = summary.uniqueDocuments
    ? Math.round((summary.availableDocuments / summary.uniqueDocuments) * 100)
    : 0;
  const missingCounts = Object.fromEntries(
    documentKeys.map((key) => [
      key,
      summary.missingPhaseDocuments.filter((phaseDocument) => phaseDocument.group === key).length
    ])
  );
  const visibleActions = actionsExpanded ? summary.myActions : summary.myActions.slice(0, 6);
  const hiddenActionCount = Math.max(0, summary.myActions.length - visibleActions.length);

  return (
    <div className="border rounded p-3 bg-white mb-4">
      <div className="d-flex justify-content-between align-items-center gap-3 mb-3 flex-wrap">
        <div>
          <h4 className="mb-1">{title}</h4>
          <div className="text-muted small">{subtitle}</div>
        </div>

        <Link to={route} className="btn btn-outline-primary">
          Open lifecycle
        </Link>
      </div>

      {error && (
        <div className="alert alert-warning py-2">
          {title} summary is unavailable: {error}
        </div>
      )}

      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <div className="border rounded p-3 h-100">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fw-semibold">Phase status</div>
              <span className="badge text-bg-secondary">
                {summary.completePhases} / {summary.totalPhases} complete
              </span>
            </div>
            <div className="progress mb-3" style={{ height: "10px" }}>
              <div
                className="progress-bar bg-success"
                role="progressbar"
                style={{ width: `${phaseProgress}%` }}
                aria-valuenow={phaseProgress}
                aria-valuemin="0"
                aria-valuemax="100"
              />
            </div>
            <div className="d-flex flex-wrap gap-2">
              {statusOptions.map((status) => (
                <span key={status.value} className={`badge ${status.className}`}>
                  {status.label}: {summary.statusCounts[status.value] ?? 0}
                </span>
              ))}
            </div>

            <div className="mt-3 d-flex flex-column gap-2">
              {phases.map((phase) => {
                const status = getStatusOption(phase.manualStatus);

                return (
                  <div key={phase.id}>
                    <div className="d-flex justify-content-between align-items-center gap-2 small mb-1">
                      <span className="text-truncate">
                        {phase.orderNo}.{phase.name}
                      </span>
                      <span className={`badge ${status.className}`}>
                        {status.label}
                      </span>
                    </div>
                    <div className="phase-track" title={status.label}>
                      <div
                        className={`phase-fill ${getPhaseBarClass(phase.manualStatus)}`}
                        style={{ width: getPhaseBarWidth(phase.manualStatus) }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="border rounded p-3 h-100">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fw-semibold">Document readiness</div>
              <span className="badge text-bg-secondary">
                {summary.availableDocuments} / {summary.uniqueDocuments} available
              </span>
            </div>
            <div className="progress mb-3" style={{ height: "10px" }}>
              <div
                className="progress-bar bg-primary"
                role="progressbar"
                style={{ width: `${documentProgress}%` }}
                aria-valuenow={documentProgress}
                aria-valuemin="0"
                aria-valuemax="100"
              />
            </div>
            <div className="d-flex flex-wrap gap-2">
              {documentKeys.map((key) => (
                <span key={key} className="badge text-bg-light text-dark border">
                  {missingLabels[key]}: {missingCounts[key] ?? 0}
                </span>
              ))}
            </div>

            <div className="document-readiness-list mt-3">
              {summary.documentTypes.map((documentType) => {
                const instance = documentType.latestInstance;

                return (
                  <div
                    key={documentType.id}
                    className="d-flex justify-content-between align-items-start gap-2 py-1 border-bottom"
                  >
                    <div className="small">
                      {instance ? (
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none text-start"
                          title={instance.originalFileName}
                          onClick={() => openProtectedFile(instance.fileUrl, instance.originalFileName)}
                        >
                          {documentType.title}
                        </button>
                      ) : (
                        <span className="text-muted">{documentType.title}</span>
                      )}
                    </div>
                    <span className={`badge ${instance ? "text-bg-success" : "text-bg-secondary"}`}>
                      {instance ? "Available" : "Missing"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-6">
          <div className="border rounded p-3 h-100">
            <div className="fw-semibold mb-2">Needs attention</div>
            {summary.attentionPhases.length === 0 ? (
              <div className="text-muted">No phases need attention.</div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {summary.attentionPhases.slice(0, 6).map((phase) => {
                  const status = getStatusOption(phase.manualStatus);
                  const missingCount = getPhaseDocuments(phase, documentKeys)
                    .filter((phaseDocument) => !phaseDocument.documentType.latestInstance)
                    .length;

                  return (
                    <div key={phase.id} className="d-flex justify-content-between gap-2">
                      <div>{phase.orderNo}.{phase.name}</div>
                      <div className="text-end">
                        <span className={`badge ${status.className} me-1`}>
                          {status.label}
                        </span>
                        {missingCount > 0 && (
                          <span className="badge text-bg-warning">
                            {missingCount} missing
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="col-lg-6">
          <div className="border rounded p-3 h-100">
            <div className="fw-semibold mb-2">{actionLabel}</div>
            {summary.myActions.length === 0 ? (
              <div className="text-muted">No missing documents assigned to your roles.</div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {visibleActions.map((phaseDocument) => (
                  <div key={`${phaseDocument.phase.id}-${phaseDocument.id}`}>
                    <div>{phaseDocument.documentType.title}</div>
                    <div className="text-muted small">
                      {phaseDocument.phase.orderNo}.{phaseDocument.phase.name} Â· {missingLabels[phaseDocument.group]}
                    </div>
                  </div>
                ))}
                {summary.myActions.length > 6 && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm align-self-start p-0"
                    onClick={() => setActionsExpanded((current) => !current)}
                  >
                    {actionsExpanded ? "Show less" : `Show ${hiddenActionCount} more`}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const en50716DocumentKeys = ["inputs", "outputs"];
const en50126DocumentKeys = ["deliverables"];

export default function Home() {
  return (
    <div className="container mt-4">
      <style>
        {`
          @keyframes phaseProgressGlow {
            0% { box-shadow: inset 0 0 0 rgba(13, 110, 253, 0); }
            50% { box-shadow: inset 0 0 10px rgba(13, 110, 253, 0.35); }
            100% { box-shadow: inset 0 0 0 rgba(13, 110, 253, 0); }
          }

          .phase-track {
            height: 8px;
            width: 100%;
            overflow: hidden;
            border-radius: 4px;
            background: #e9ecef;
          }

          .phase-fill {
            height: 100%;
            border-radius: 4px;
            transition: width 180ms ease;
          }

          .phase-fill-complete {
            background: #198754;
          }

          .phase-fill-progress {
            background: linear-gradient(90deg, #0d6efd 0%, #6ea8fe 100%);
            animation: phaseProgressGlow 1.8s ease-in-out infinite;
          }

          .phase-fill-blocked {
            background: #dc3545;
          }

          .phase-fill-muted {
            background: #adb5bd;
          }

          .phase-fill-empty {
            background: transparent;
          }

          .document-readiness-list > div:last-child {
            border-bottom: 0 !important;
          }

          .requirement-donut {
            width: 112px;
            height: 112px;
            flex: 0 0 auto;
          }

          .requirement-donut-ring {
            width: 112px;
            height: 112px;
            border-radius: 50%;
            box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.06);
          }

          .requirement-donut-center {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 64px;
            height: 64px;
            transform: translate(-50%, -50%);
            border-radius: 50%;
            background: #ffffff;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.04);
          }

          .requirement-donut-dot {
            width: 10px;
            height: 10px;
            border-radius: 999px;
            display: inline-block;
            flex: 0 0 auto;
          }
        `}
      </style>

      <div className="d-flex justify-content-between align-items-start gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="mb-1">Welcome</h2>
          <div className="text-muted">
            Lifecycle snapshots and project workspace.
          </div>
        </div>

        <img
          src={etcsdmi}
          alt="ETCS DMI"
          style={{ width: "220px", maxWidth: "100%", borderRadius: "8px" }}
        />
      </div>

      <div className="row g-3 mb-4">
        <SystemRequirementsSnapshot />
        <RequirementsSnapshot />
        <TestsCoverageSnapshot />
      </div>

      <LifecyclePanel
        title="EN50716 Lifecycle"
        subtitle="Current state snapshot. Completed phases can return to in progress."
        endpoint="/api/en50716/lifecycle"
        route="/en50716-docs"
        documentKeys={en50716DocumentKeys}
        missingLabels={{ inputs: "Missing inputs", outputs: "Missing outputs" }}
        actionLabel="My document actions"
        actionFilter={(phaseDocument) => !isPreviousPhaseReference(phaseDocument)}
      />

      <LifecyclePanel
        title="EN50126 RAMS Lifecycle"
        subtitle="RAMS deliverables snapshot. Updates can revise existing deliverables."
        endpoint="/api/en50126/lifecycle"
        route="/en50126-docs"
        documentKeys={en50126DocumentKeys}
        missingLabels={{ deliverables: "Missing deliverables" }}
        actionLabel="My deliverable actions"
        actionFilter={() => true}
      />
    </div>
  );
}
